/**
 * JITApprovalQueuePanel - JIT Access Approval Dashboard
 *
 * FERP CRITICAL: Approve/reject pending JIT access requests
 *
 * Features:
 * - Pending requests queue with real-time updates
 * - Request details with justification
 * - Approve/reject actions with reason
 * - Duration override capability
 * - Emergency request highlighting
 * - Complete audit trail
 * - Filter by status
 *
 * Architecture Principles:
 * - Data-driven (all requests from backend)
 * - Repository pattern (JITAccessRepository)
 * - Real-time updates (network-only fetch)
 * - Immutable audit trails
 *
 * GitHub Issue: #343
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Stack,
  Title,
  Text,
  Table,
  Button,
  Alert,
  Group,
  Card,
  Badge,
  Loader,
  Modal,
  Textarea,
  NumberInput,
  ActionIcon,
  Tooltip,
  Box,
  Select,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconAlertCircle,
  IconClock,
  IconUser,
  IconShield,
  IconRefresh,
} from '@tabler/icons-react';
import { useApolloClient } from '@apollo/client/react';
import { ApolloClient } from '@apollo/client';
import {
  JITAccessRepository,
  JITAccessRequest,
  ApproveJITRequestInput,
  RejectJITRequestInput,
  JITRequestFilter,
} from '../data/repositories/JITAccessRepository';

// Status constants (matches generated GraphQL enums)
const JITRequestStatus = {
  PENDING: 'PENDING',
} as const;

export interface JITApprovalQueuePanelProps {
  /** Tenant ID for multi-tenant isolation */
  tenantId: string;
  /** Current user ID (approver) */
  approverId: string;
  /** Current user email */
  approverEmail: string;
  /** Callback when request is approved */
  onApprove?: (requestId: string) => void;
  /** Callback when request is rejected */
  onReject?: (requestId: string) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
}

type ActionMode = 'approve' | 'reject' | null;

/**
 * JIT Approval Queue Panel
 *
 * FERP Architecture Compliance:
 * - ✅ Data-driven (requests from backend)
 * - ✅ Repository pattern (JITAccessRepository)
 * - ✅ Real-time updates
 * - ✅ Immutable audit trails
 */
export function JITApprovalQueuePanel({
  tenantId,
  approverId,
  approverEmail,
  onApprove,
  onReject,
  onError,
}: JITApprovalQueuePanelProps) {
  const apolloClient = useApolloClient() as ApolloClient;
  const repository = useMemo(() => new JITAccessRepository(apolloClient), [apolloClient]);

  // State
  const [requests, setRequests] = useState<JITAccessRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('PENDING');

  // Modal state
  const [selectedRequest, setSelectedRequest] = useState<JITAccessRequest | null>(null);
  const [actionMode, setActionMode] = useState<ActionMode>(null);
  const [actionReason, setActionReason] = useState<string>('');
  const [overrideDuration, setOverrideDuration] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  /**
   * Load pending requests
   */
  const loadRequests = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);

      const filter: JITRequestFilter = {
        tenantId,
        status: statusFilter !== 'ALL' ? statusFilter : undefined,
        limit: 100,
      };

      const result = await repository.listRequests(filter);
      setRequests(result.requests);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load requests';
      setError(errorMsg);
      if (onError) {
        onError(err instanceof Error ? err : new Error(errorMsg));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [tenantId, statusFilter, repository, onError]);

  /**
   * Initial load
   */
  useEffect(() => {
    loadRequests(true);
  }, [loadRequests]);

  /**
   * Auto-refresh every 30 seconds
   */
  useEffect(() => {
    const interval = setInterval(() => {
      loadRequests(false);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [loadRequests]);

  /**
   * Open approval modal
   */
  const handleOpenApprove = useCallback((request: JITAccessRequest) => {
    setSelectedRequest(request);
    setActionMode('approve');
    setActionReason('');
    setOverrideDuration(null);
  }, []);

  /**
   * Open rejection modal
   */
  const handleOpenReject = useCallback((request: JITAccessRequest) => {
    setSelectedRequest(request);
    setActionMode('reject');
    setActionReason('');
  }, []);

  /**
   * Close modal
   */
  const handleCloseModal = useCallback(() => {
    setSelectedRequest(null);
    setActionMode(null);
    setActionReason('');
    setOverrideDuration(null);
  }, []);

  /**
   * Handle approve action
   */
  const handleApprove = useCallback(async () => {
    if (!selectedRequest) return;

    try {
      setSubmitting(true);
      setError(null);

      const input: ApproveJITRequestInput = {
        tenantId,
        requestId: selectedRequest.requestId,
        approverId,
        approverEmail,
        durationMinutes: overrideDuration ?? undefined,
      };

      await repository.approveRequest(input);

      // Refresh list
      await loadRequests(false);

      // Close modal
      handleCloseModal();

      if (onApprove) {
        onApprove(selectedRequest.requestId);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to approve request';
      setError(errorMsg);
      if (onError) {
        onError(err instanceof Error ? err : new Error(errorMsg));
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    selectedRequest,
    tenantId,
    approverId,
    approverEmail,
    overrideDuration,
    repository,
    loadRequests,
    handleCloseModal,
    onApprove,
    onError,
  ]);

  /**
   * Handle reject action
   */
  const handleReject = useCallback(async () => {
    if (!selectedRequest || !actionReason.trim()) return;

    try {
      setSubmitting(true);
      setError(null);

      const input: RejectJITRequestInput = {
        tenantId,
        requestId: selectedRequest.requestId,
        rejectedBy: approverId,
        reason: actionReason.trim(),
      };

      await repository.rejectRequest(input);

      // Refresh list
      await loadRequests(false);

      // Close modal
      handleCloseModal();

      if (onReject) {
        onReject(selectedRequest.requestId);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to reject request';
      setError(errorMsg);
      if (onError) {
        onError(err instanceof Error ? err : new Error(errorMsg));
      }
    } finally {
      setSubmitting(false);
    }
  }, [
    selectedRequest,
    actionReason,
    tenantId,
    approverId,
    repository,
    loadRequests,
    handleCloseModal,
    onReject,
    onError,
  ]);

  /**
   * Format timestamp
   */
  const formatTimestamp = useCallback((timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffMins < 1440) {
      return `${Math.floor(diffMins / 60)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  }, []);

  /**
   * Get status badge color
   */
  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'PENDING':
        return 'yellow';
      case 'APPROVED':
        return 'green';
      case 'REJECTED':
        return 'red';
      case 'EXPIRED':
        return 'gray';
      case 'REVOKED':
        return 'orange';
      default:
        return 'blue';
    }
  }, []);

  // Loading state
  if (loading) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="center" py="xl">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">Loading approval queue...</Text>
        </Group>
      </Card>
    );
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={3}>JIT Approval Queue</Title>
            <Text size="sm" c="dimmed">
              Review and approve pending JIT access requests
            </Text>
          </div>
          <Group gap="xs">
            <Select
              size="sm"
              value={statusFilter}
              onChange={(value: string | null) => setStatusFilter(value || 'PENDING')}
              data={[
                { value: 'ALL', label: 'All' },
                { value: 'PENDING', label: 'Pending' },
                { value: 'APPROVED', label: 'Approved' },
                { value: 'REJECTED', label: 'Rejected' },
              ]}
              style={{ width: 150 }}
            />
            <Tooltip label="Refresh">
              <ActionIcon
                variant="light"
                onClick={() => loadRequests(false)}
                loading={refreshing}
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {/* Error message */}
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
            {error}
          </Alert>
        )}

        {/* Requests table */}
        {requests.length === 0 ? (
          <Alert icon={<IconAlertCircle size={16} />} color="gray">
            No {statusFilter !== 'ALL' && statusFilter.toLowerCase()} requests found
          </Alert>
        ) : (
          <Box style={{ overflowX: 'auto' }}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Requester</Table.Th>
                  <Table.Th>Role</Table.Th>
                  <Table.Th>Duration</Table.Th>
                  <Table.Th>Justification</Table.Th>
                  <Table.Th>Requested</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {requests.map((request) => (
                  <Table.Tr key={request.requestId}>
                    {/* Requester */}
                    <Table.Td>
                      <Group gap="xs">
                        <IconUser size={16} />
                        <div>
                          <Text size="sm" fw={500}>{request.requesterEmail}</Text>
                          <Text size="xs" c="dimmed">
                            Current: {request.requesterRole}
                          </Text>
                        </div>
                      </Group>
                    </Table.Td>

                    {/* Requested role */}
                    <Table.Td>
                      <Group gap="xs">
                        <IconShield size={16} />
                        <Text size="sm" fw={500}>
                          {request.requestedRoleDisplayName || request.requestedRole}
                        </Text>
                        {request.emergency && (
                          <Badge size="xs" color="red">Emergency</Badge>
                        )}
                      </Group>
                    </Table.Td>

                    {/* Duration */}
                    <Table.Td>
                      <Group gap="xs">
                        <IconClock size={16} />
                        <Text size="sm">
                          {request.durationMinutes} min
                          {request.durationMinutes >= 60 && ` (${Math.floor(request.durationMinutes / 60)}h)`}
                        </Text>
                      </Group>
                    </Table.Td>

                    {/* Justification */}
                    <Table.Td>
                      <Tooltip label={request.justification} multiline w={300}>
                        <Text size="sm" lineClamp={2} style={{ maxWidth: 200 }}>
                          {request.justification}
                        </Text>
                      </Tooltip>
                    </Table.Td>

                    {/* Timestamp */}
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {formatTimestamp(request.createdAt)}
                      </Text>
                    </Table.Td>

                    {/* Status */}
                    <Table.Td>
                      <Badge color={getStatusColor(request.status)} variant="light">
                        {request.status}
                      </Badge>
                    </Table.Td>

                    {/* Actions */}
                    <Table.Td>
                      {request.status === JITRequestStatus.PENDING ? (
                        <Group gap="xs">
                          <Tooltip label="Approve">
                            <ActionIcon
                              variant="light"
                              color="green"
                              onClick={() => handleOpenApprove(request)}
                            >
                              <IconCheck size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label="Reject">
                            <ActionIcon
                              variant="light"
                              color="red"
                              onClick={() => handleOpenReject(request)}
                            >
                              <IconX size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      ) : (
                        <Text size="sm" c="dimmed">-</Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        )}

        {/* Approve Modal */}
        <Modal
          opened={actionMode === 'approve' && selectedRequest !== null}
          onClose={handleCloseModal}
          title="Approve JIT Access Request"
          size="md"
        >
          {selectedRequest && (
            <Stack gap="md">
              <Alert icon={<IconAlertCircle size={16} />} color="blue">
                <Stack gap="xs">
                  <Text size="sm" fw={500}>Request Details</Text>
                  <Group gap="xs">
                    <Text size="sm">Requester:</Text>
                    <Text size="sm" fw={500}>{selectedRequest.requesterEmail}</Text>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm">Role:</Text>
                    <Text size="sm" fw={500}>{selectedRequest.requestedRole}</Text>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm">Duration:</Text>
                    <Text size="sm" fw={500}>
                      {selectedRequest.durationMinutes} minutes ({Math.floor(selectedRequest.durationMinutes / 60)}h {selectedRequest.durationMinutes % 60}m)
                    </Text>
                  </Group>
                  <Box>
                    <Text size="sm">Justification:</Text>
                    <Text size="sm" c="dimmed">{selectedRequest.justification}</Text>
                  </Box>
                </Stack>
              </Alert>

              <NumberInput
                label="Override Duration (optional)"
                placeholder="Leave empty to use requested duration"
                description="Reduce the requested duration if needed (minutes)"
                value={overrideDuration ?? ''}
                onChange={(value: number | string) => setOverrideDuration(typeof value === 'number' ? value : null)}
                min={15}
                max={selectedRequest.durationMinutes}
                step={15}
                leftSection={<IconClock size={16} />}
              />

              <Group justify="flex-end" gap="xs">
                <Button variant="light" onClick={handleCloseModal} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  color="green"
                  onClick={handleApprove}
                  loading={submitting}
                  leftSection={<IconCheck size={16} />}
                >
                  Approve Request
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>

        {/* Reject Modal */}
        <Modal
          opened={actionMode === 'reject' && selectedRequest !== null}
          onClose={handleCloseModal}
          title="Reject JIT Access Request"
          size="md"
        >
          {selectedRequest && (
            <Stack gap="md">
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                <Stack gap="xs">
                  <Text size="sm" fw={500}>Request Details</Text>
                  <Group gap="xs">
                    <Text size="sm">Requester:</Text>
                    <Text size="sm" fw={500}>{selectedRequest.requesterEmail}</Text>
                  </Group>
                  <Group gap="xs">
                    <Text size="sm">Role:</Text>
                    <Text size="sm" fw={500}>{selectedRequest.requestedRole}</Text>
                  </Group>
                </Stack>
              </Alert>

              <Textarea
                label="Rejection Reason"
                placeholder="Explain why this request is being rejected"
                description="A clear reason will be sent to the requester"
                value={actionReason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setActionReason(e.currentTarget.value)}
                minRows={3}
                required
                error={
                  actionReason.length > 0 && actionReason.trim().length < 10
                    ? 'Minimum 10 characters required'
                    : undefined
                }
              />

              <Group justify="flex-end" gap="xs">
                <Button variant="light" onClick={handleCloseModal} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  color="red"
                  onClick={handleReject}
                  loading={submitting}
                  disabled={actionReason.trim().length < 10}
                  leftSection={<IconX size={16} />}
                >
                  Reject Request
                </Button>
              </Group>
            </Stack>
          )}
        </Modal>
      </Stack>
    </Card>
  );
}
